import React, { useReducer, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import qs from 'qs';
import noop from 'lodash/noop';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { isValidEmail } from 'utils/textFormats';
import { SuiContainer, SuiModalContent, SuiTextInput, SuiButton } from '@splashthat/sui';
import Recaptcha from 'common/components/Recaptcha';

import { HeaderText, RecaptchaContainer, SubmitButtonContainer, Asterisk, InputContainer } from './Form.styles';
import {
    formKeys,
    STATUS_IDLE,
    STATUS_SUCCESS,
    STATUS_LOADING,
    STATUS_ERROR,
    initState,
    reducer,
    setValue,
    setError,
    clearErrors,
    setValidating,
    setSubmissionStatus,
    getFormValue,
    getFormError,
    getSubmissionStatus,
    getAnnouncementError,
} from './Form.state';

const bootstrap = getBootstrap();

const HEADER_TITLE = 'Contact the Organizer';
const LABEL_NAME = 'Name';
const PLACEHOLDER_NAME = 'Enter your name';
const LABEL_EMAIL = 'Email';
const PLACEHOLDER_EMAIL = 'you@you.email';
const LABEL_COMPANY = 'Company';
const LABEL_NOTE = 'Message';
const LABEL_CAPTCHA = 'reCAPTCHA';
const LABEL_SUBMISSION_ERROR = 'There has been an error submitting the note.';
const LABEL_SEND_MESSAGE = 'Submit';
const LABEL_MESSAGE_SENT = 'Message sent!';
const LABEL_SENDING_MESSAGE = 'Sending message...';
const LABEL_TRY_AGAIN = 'Try again';

const EMAIL_ERROR_ID = 'contact-organizer-email-error';
const NOTE_ERROR_ID = 'contact-organizer-note-error';

const Form = (props) => {
    const [state, dispatch] = useReducer(reducer, initState);
    const emailInputRef = useRef(null);
    const noteInputRef = useRef(null);

    const captchaToken = getFormValue(state, formKeys.CAPTCHA_TOKEN);
    const name = getFormValue(state, formKeys.NAME);
    const email = getFormValue(state, formKeys.EMAIL);
    const company = getFormValue(state, formKeys.COMPANY);
    const note = getFormValue(state, formKeys.NOTE);
    const emailError = getFormError(state, formKeys.EMAIL);
    const noteError = getFormError(state, formKeys.NOTE);
    const captchaError = getFormError(state, formKeys.CAPTCHA_TOKEN);
    const submissionStatus = getSubmissionStatus(state);
    const announcementError = getAnnouncementError(state);

    useEffect(() => {
        if (submissionStatus === STATUS_SUCCESS) {
            props.onSubmissionSuccess();
        }
    }, [submissionStatus, props.onSubmissionSuccess]);

    const submitForm = () => {
        const requestBody = {
            action: 'sendANote',
            eventid: bootstrap.get('eventId'),
            name,
            email,
            company,
            note,
            captchaValue: captchaToken,
        };

        dispatch(setSubmissionStatus(STATUS_LOADING));
        axios({
            method: 'POST',
            url: '',
            headers: { 'content-type': 'application/x-www-form-urlencoded' },
            data: qs.stringify(requestBody),
        })
            .then((res) => {
                if (res && res.data && res.data.error) {
                    throw res.data.error;
                }
                return res;
            })
            .then((_) => dispatch(setSubmissionStatus(STATUS_SUCCESS)))
            .catch((_) => dispatch(setSubmissionStatus(STATUS_ERROR)));
    };

    const validateForm = () => {
        // Start validation attempt - enables captcha error announcement
        dispatch(setValidating(true));

        // Clear previous errors before validating
        dispatch(clearErrors());

        const emailValue = (email || '').trim();
        const noteValue = (note || '').trim();

        const errors = [
            !emailValue && { key: formKeys.EMAIL, message: 'This value is required.' },
            emailValue &&
                !isValidEmail(emailValue) && { key: formKeys.EMAIL, message: 'Please, insert a valid email.' },
            !noteValue && { key: formKeys.NOTE, message: 'This value is required.' },
            !captchaToken && { key: formKeys.CAPTCHA_TOKEN, message: 'This value is required.' },
        ].filter(Boolean);

        // Set new errors directly without using a separate validation attempt counter
        errors.forEach(({ key, message }) => dispatch(setError(key, message)));

        const isFormValid = errors.length === 0;

        // Focus first invalid field - aria-describedby will announce the error once
        if (!isFormValid && errors.some((err) => err.key === formKeys.EMAIL) && emailInputRef.current) {
            emailInputRef.current.focus();
        } else if (!isFormValid && errors.some((err) => err.key === formKeys.NOTE) && noteInputRef.current) {
            noteInputRef.current.focus();
        }

        return isFormValid;
    };

    return (
        <SuiContainer variation="border standard flex-vertical">
            <SuiModalContent>
                <HeaderText>{HEADER_TITLE}</HeaderText>
                <InputContainer>
                    <SuiTextInput
                        label={LABEL_NAME}
                        variation={'form'}
                        placeholder={PLACEHOLDER_NAME}
                        fullWidth
                        autoComplete="name"
                        errorText={getFormError(state, formKeys.NAME)}
                        onChange={(_, value) => dispatch(setValue(formKeys.NAME, value))}
                    />
                </InputContainer>
                <InputContainer>
                    <SuiTextInput
                        ref={emailInputRef}
                        label={
                            <p>
                                {LABEL_EMAIL}
                                <Asterisk> *</Asterisk>
                            </p>
                        }
                        placeholder={PLACEHOLDER_EMAIL}
                        variation={'form'}
                        type="email"
                        fullWidth
                        autoComplete="email"
                        errorText={emailError}
                        aria-invalid={!!emailError}
                        aria-describedby={emailError ? EMAIL_ERROR_ID : undefined}
                        onChange={(_, value) => dispatch(setValue(formKeys.EMAIL, value))}
                    />
                    {emailError && <VisuallyHidden id={EMAIL_ERROR_ID}>{emailError}</VisuallyHidden>}
                </InputContainer>
                <InputContainer>
                    <SuiTextInput
                        label={LABEL_COMPANY}
                        variation={'form'}
                        fullWidth
                        errorText={getFormError(state, formKeys.COMPANY)}
                        onChange={(_, value) => dispatch(setValue(formKeys.COMPANY, value))}
                    />
                </InputContainer>
                <InputContainer>
                    <SuiTextInput
                        ref={noteInputRef}
                        label={
                            <p>
                                {LABEL_NOTE}
                                <Asterisk> *</Asterisk>
                            </p>
                        }
                        multiLine
                        rows={5}
                        fullWidth
                        variation={'form'}
                        errorText={noteError}
                        aria-invalid={!!noteError}
                        aria-describedby={noteError ? NOTE_ERROR_ID : undefined}
                        onChange={(_, value) => dispatch(setValue(formKeys.NOTE, value))}
                    />
                    {noteError && <VisuallyHidden id={NOTE_ERROR_ID}>{noteError}</VisuallyHidden>}
                </InputContainer>
                {submissionStatus !== STATUS_SUCCESS && (
                    <RecaptchaContainer>
                        <Recaptcha
                            onVerify={(token) => {
                                dispatch(setValue(formKeys.CAPTCHA_TOKEN, token));
                            }}
                            onExpired={() => {
                                dispatch(setValue(formKeys.CAPTCHA_TOKEN, null));
                            }}
                            aria-invalid={!!captchaError}
                        />
                        {announcementError?.key === formKeys.CAPTCHA_TOKEN && (
                            <VisuallyHidden role="alert" aria-live="assertive" aria-atomic="true">
                                {`${LABEL_CAPTCHA}: ${announcementError.message}`}
                            </VisuallyHidden>
                        )}
                    </RecaptchaContainer>
                )}
                <SubmitButtonContainer>
                    {
                        {
                            [STATUS_IDLE]: (
                                <SuiButton
                                    label={LABEL_SEND_MESSAGE}
                                    variation="small"
                                    onClick={() => {
                                        if (validateForm()) {
                                            submitForm();
                                        }
                                    }}
                                />
                            ),
                            [STATUS_LOADING]: <SuiButton label={LABEL_SENDING_MESSAGE} variation="small" disabled />,
                            [STATUS_SUCCESS]: <SuiButton label={LABEL_MESSAGE_SENT} variation="small" disabled />,
                            [STATUS_ERROR]: (
                                <React.Fragment>
                                    <SuiButton
                                        label={LABEL_TRY_AGAIN}
                                        variation="small"
                                        onClick={() => {
                                            if (validateForm()) {
                                                submitForm();
                                            }
                                        }}
                                    />
                                    <p>{LABEL_SUBMISSION_ERROR}</p>
                                </React.Fragment>
                            ),
                        }[submissionStatus]
                    }
                </SubmitButtonContainer>
            </SuiModalContent>
        </SuiContainer>
    );
};

Form.propTypes = {
    onSubmissionSuccess: PropTypes.func,
};

Form.defaultProps = {
    onSubmissionSuccess: noop,
};

export default Form;
